/**
 * Who is buying — this period and over their lifetime — and who is new.
 *
 * The top spenders as bars, the newcomers as a list, and a click into any of
 * them for their whole history: what they have ordered and what came back. Only
 * the orders that carried a customer count here; a walk-in leaves no name, so it
 * cannot appear in a report about names.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { HBars } from './Charts.jsx';
import { ReportTabs } from './ReportTabs.jsx';
import { usePeriod, PeriodBar, prettyDay } from './period.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

const CHANNEL = {
  dine_in: 'Dining',
  takeaway: 'Takeaway',
  delivery: 'Delivery',
  foodpanda: 'Foodpanda',
  phone_order: 'Phone',
  online: 'Online',
};

export default function CustomersPage() {
  const period = usePeriod('day');
  const [openId, setOpenId] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'customers', period.from, period.to],
    queryFn: () => api.get(withQuery('/reports/customers', { from: period.from, to: period.to })),
    placeholderData: (previous) => previous,
  });

  const s = data?.summary;

  return (
    <div className="mx-auto max-w-5xl p-4">
      <Header period={period} />

      {isLoading && !data ? (
        <div className="flex h-[40vh] items-center justify-center">
          <Spinner label="Loading" />
        </div>
      ) : isError || !data ? (
        <p className="py-20 text-center text-sm text-ink-2">Could not load this report.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Figure label="Bought this period" value={s.activeInPeriod} sub="customers with an order" />
            <Figure label="New this period" value={s.newInPeriod} sub="first seen" />
            <Figure label="Return more than once" value={s.repeat} sub="of all time" />
            <Figure label="Average spend" value={formatPKR(s.avgSpent)} sub="per customer, lifetime" />
          </div>

          <Panel title="Top customers this period" className="mt-4">
            {(data.topCustomers ?? []).length === 0 ? (
              <Empty note="No named customers bought this period — likely all walk-ins." />
            ) : (
              <HBars
                data={data.topCustomers}
                labelOf={(r) => r.label}
                valueOf={(r) => Number(r.revenue) || 0}
                subOf={(r) => `${r.orders}×`}
                formatValue={formatPKR}
                onItem={(r) => r.id && setOpenId(r.id)}
              />
            )}
            {(data.topCustomers ?? []).length > 0 && (
              <p className="mt-3 text-xs text-ink-3">Tap a customer to see their history.</p>
            )}
          </Panel>

          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            <Panel title="New customers">
              <PeopleList
                rows={data.newCustomers}
                empty="No new customers this period."
                onOpen={setOpenId}
                right={(r) => `${r.total_orders ?? 0} orders`}
                sub={(r) => `Joined ${prettyDay(r.joined)}`}
              />
            </Panel>

            <Panel title="Refunds to customers">
              {(data.refundsByCustomer ?? []).length === 0 ? (
                <Empty note="No refunds to named customers." />
              ) : (
                <PeopleList
                  rows={data.refundsByCustomer}
                  onOpen={setOpenId}
                  idOf={(r) => r.id}
                  nameOf={(r) => r.label}
                  right={(r) => formatPKR(Number(r.amount) || 0)}
                  sub={(r) => `${r.refunds} refund${r.refunds === 1 ? '' : 's'}`}
                  rightTone
                />
              )}
            </Panel>
          </div>
        </>
      )}

      {openId && <CustomerDrawer id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

/** One customer's whole record — the purchase-history drill. */
function CustomerDrawer({ id, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'customer', id],
    queryFn: () => api.get(`/reports/customers/${id}`),
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Customer history"
      className="fixed inset-0 z-30 flex items-end justify-center bg-black/70 sm:items-center"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-surface p-5 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading || !data ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner label="Loading" />
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-semibold text-lg text-ink">{data.customer.name || 'Customer'}</h2>
                <p className="text-sm text-ink-3">
                  {data.customer.phone || 'no phone'}
                  {data.customer.joined ? ` · joined ${prettyDay(data.customer.joined)}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-2.5 py-1 text-sm text-ink-2 hover:bg-surface-3"
              >
                Close
              </button>
            </div>

            <div className="mb-4 grid grid-cols-3 gap-2">
              <Mini label="Orders" value={data.customer.total_orders ?? data.orders.length} />
              <Mini label="Spent" value={formatPKR(Number(data.customer.total_spent) || 0)} />
              <Mini label="Points" value={data.customer.loyalty_points ?? 0} />
            </div>

            <h3 className="mb-2 text-sm font-medium text-ink-2">Orders</h3>
            {data.orders.length === 0 ? (
              <Empty note="No orders." />
            ) : (
              <ul className="mb-4 space-y-1">
                {data.orders.map((o, i) => (
                  <li
                    key={i}
                    className="flex items-baseline justify-between gap-2 rounded-lg border border-rule bg-surface-3 px-3 py-2"
                  >
                    <span className="min-w-0 truncate text-sm text-ink">
                      {o.order_number}
                      <span className="ml-2 text-xs text-ink-3">
                        {CHANNEL[o.channel] ?? o.channel} · {prettyDay(o.date)}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm font-medium tabular-nums text-ink">
                      {formatPKR(Number(o.amount) || 0)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {data.refunds.length > 0 && (
              <>
                <h3 className="mb-2 text-sm font-medium text-ink-2">Refunds</h3>
                <ul className="space-y-1">
                  {data.refunds.map((r, i) => (
                    <li key={i} className="flex items-baseline justify-between gap-2 text-sm">
                      <span className="min-w-0 truncate text-ink-2">
                        {r.order_number}
                        <span className="ml-2 text-xs text-ink-3">{r.reason || '—'}</span>
                      </span>
                      <span className="shrink-0 tabular-nums text-warn">
                        −{formatPKR(Number(r.amount) || 0)}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PeopleList({ rows, empty, onOpen, idOf = (r) => r.id, nameOf = (r) => r.name, right, sub, rightTone }) {
  if (!rows || rows.length === 0) return <Empty note={empty} />;
  return (
    <ul className="space-y-1">
      {rows.slice(0, 12).map((r, i) => {
        const id = idOf(r);
        const inner = (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-ink">{nameOf(r) || 'Customer'}</span>
              {sub && <span className="block text-xs text-ink-3">{sub(r)}</span>}
            </span>
            <span className={`shrink-0 text-sm font-medium tabular-nums ${rightTone ? 'text-warn' : 'text-ink-2'}`}>
              {right(r)}
            </span>
          </>
        );
        return (
          <li key={id ?? i}>
            {id ? (
              <button
                type="button"
                onClick={() => onOpen(id)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-surface-3"
              >
                {inner}
              </button>
            ) : (
              <div className="flex items-center gap-2 px-2 py-1.5">{inner}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-lg border border-rule bg-surface-3 px-3 py-2 text-center">
      <p className="text-xs text-ink-3">{label}</p>
      <p className="mt-0.5 font-medium tabular-nums text-ink">{value}</p>
    </div>
  );
}

function Empty({ note }) {
  return <p className="py-6 text-center text-sm text-ink-3">{note}</p>;
}

function Figure({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-rule bg-surface p-3.5">
      <p className="text-xs text-ink-3">{label}</p>
      <p className="mt-1 font-semibold text-xl tabular-nums text-ink">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-ink-3">{sub}</p>}
    </div>
  );
}

function Panel({ title, children, className = '' }) {
  return (
    <section className={`rounded-xl border border-rule bg-surface p-3.5 ${className}`}>
      <h2 className="mb-3 text-sm font-medium text-ink-2">{title}</h2>
      {children}
    </section>
  );
}

function Header({ period }) {
  return (
    <div className="mb-4">
      <ReportTabs />
      <PeriodBar {...period} />
    </div>
  );
}
